import { z } from 'zod';

const CURRENCY_CODE = /^[A-Z]{3}$/;
const DECIMAL = /^(?:\d+\.?\d*|\.\d+)$/;
// Mirrors the backend's upper bound on PUT /platform/v1/ops/exchange-rates.
export const MAX_RATE = 1_000_000;

const currencyCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(CURRENCY_CODE, { error: 'exchangeRates:validation.currency' });

const rateValue = z
  .string()
  .trim()
  .min(1, { error: 'exchangeRates:validation.rateRequired' })
  .regex(DECIMAL, { error: 'exchangeRates:validation.rateNumber' })
  .refine((value) => Number(value) > 0, { error: 'exchangeRates:validation.ratePositive' })
  .refine((value) => Number(value) <= MAX_RATE, { error: 'exchangeRates:validation.rateMax' });

export const rateRowSchema = z
  .object({ baseCurrency: currencyCode, quoteCurrency: currencyCode, rate: rateValue })
  .superRefine((row, ctx) => {
    if (row.baseCurrency === row.quoteCurrency) {
      ctx.addIssue({
        code: 'custom',
        path: ['quoteCurrency'],
        message: 'exchangeRates:validation.samePair',
      });
    }
  });

/**
 * Form behind PUT /platform/v1/ops/exchange-rates. The rate stays a string here so input and
 * output types match what FormField expects; the mapper turns it into the number on the wire.
 * One pair per row: a repeated pair would make "newest wins" ambiguous.
 */
export const rateRowsSchema = z
  .object({
    rates: z.array(rateRowSchema).min(1, { error: 'exchangeRates:validation.atLeastOne' }),
  })
  .superRefine((values, ctx) => {
    const seen = new Set<string>();
    values.rates.forEach((row, index) => {
      const key = `${row.baseCurrency}/${row.quoteCurrency}`;
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['rates', index, 'quoteCurrency'],
          message: 'exchangeRates:validation.duplicatePair',
        });
      }
      seen.add(key);
    });
  });

export type RateRowInput = z.input<typeof rateRowSchema>;
export type RateRowsInput = z.input<typeof rateRowsSchema>;
export type RateRowsOutput = z.output<typeof rateRowsSchema>;

export function emptyRateRow(baseCurrency = ''): RateRowInput {
  return { baseCurrency, quoteCurrency: '', rate: '' };
}
